import React, { useCallback, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useDropzone } from "react-dropzone";
import { translateMessage, formatFileSize, formatDate } from "functions";
import { ReactComponent as UploadIcon } from "images/icons/upload.svg";
import { ReactComponent as CheckDownloadIcon } from "images/icons/check-download.svg";
import { ReactComponent as LoadingIcon } from "images/icons/loading.svg";
import axios from "axios";
import { useApiEndpoint } from "ApiEndpointContext";
import { RefreshContext, useRefresh } from "providers/RefreshProvider";
import clsx from "clsx";
import {
  typesDocumentsClient,
  typesDocumentsClientsStatuts,
  typesDocumentsClientToGestionnaire,
} from "constants/typesDocuments";
import { statutsDossierGestionnaireToClient } from "constants/statutsDossier";
import InfoTooltip from "components/InfoTooltip";
import StatusTag from "components/Status/StatusTag";
import { ReactComponent as IconDelete } from "images/icons/delete.svg";
import { NON_CONFORME } from "constants/statutsDocuments";

function validateFileSize(file) {
  if (file.size > 25 * 1024 * 1024) {
    return {
      code: "file-too-large",
      message: `Le fichier fait plus de 25 Mo.`,
    };
  }
  return null;
}

function FileRow({ type, file, setFile, removeFile, uploadFile, loading }) {
  const { Icon, label, invalid, success } = type;

  const onDrop = useCallback(
    (acceptedFiles) => {
      setFile(label, acceptedFiles[0]);
    },
    [setFile, label],
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      onDrop,
      maxFiles: 1,
      accept: { "application/pdf": [".pdf"] },
      validator: validateFileSize,
    });

  const errors = fileRejections.flatMap(
    (rejectedFile) =>
      rejectedFile?.errors?.map((error) => {
        switch (error.code) {
          case "file-invalid-type":
            return "Le fichier doit être au format PDF.";
          default:
            return error.message;
        }
      }) || [],
  );

  if (invalid && !file && !success) {
    errors.push(
      <div className="flex-0 whitespace-nowrap w-32 ml-4">
        <StatusTag label={NON_CONFORME.label} color={NON_CONFORME.color} />{" "}
        <InfoTooltip text="Ce fichier est non conforme, veuillez le redéposer." />
      </div>,
    );
  }

  return (
    <>
      <tr
        {...(!file && !success && getRootProps())}
        className={clsx(
          "dropzone w-full",
          { "bg-light-color": isDragActive },
          success ? "text-green border-green" : "text-main-color border-gray",
        )}
      >
        <td className="p-4 md:w--80">
          <input {...getInputProps()} disabled={loading} />
          <div
            className={clsx(
              "border rounded-md bg-white px-4 py-2 text-xs md:text-sm min-w-56",
              {
                "text-green": success,
                "border-blue text-main-color": !success,
              },
            )}
          >
            <Icon className="inline-block vertical-align h-4 -mt-1 mr-2" />
            {label}
          </div>
        </td>
        {!success && (
          <td className="w-[65px]">
            {file && (
              <button onClick={() => uploadFile(label)} disabled={loading}>
                {loading ? (
                  <LoadingIcon className="animate-spin m-0 h-[26px] w-[26px]" />
                ) : (
                  <CheckDownloadIcon />
                )}
              </button>
            )}
            {file && !loading && (
              <button onClick={() => removeFile(label)} className="ml-2">
                <IconDelete />
              </button>
            )}
            {!file && !success && <UploadIcon />}
          </td>
        )}
      </tr>
      {!!errors?.length && (
        <tr>
          <td colSpan="2" className="pb-2">
            {errors.map((error) => (
              <p className="!text-status-error" key={error}>
                {error}
              </p>
            ))}
          </td>
        </tr>
      )}
      <tr
        className={clsx(
          "border border-x-0 border-t-0 border-b-1 dropzone w-full",
          { "bg-light-color": isDragActive },
          success ? "text-green border-green" : "text-main-color border-gray",
        )}
      >
        {!!file && (
          <td className="pb-5 px-5" colSpan="2">
            <div className="flex gap-2">
              <span className="flex-1">{file?.name || "Sans nom"}</span>
              <span className="text-Gris-500-Soregies whitespace-nowrap">
                {formatDate(file?.lastModified)} &ndash;{" "}
                {formatFileSize(file?.size)}
              </span>
            </div>
          </td>
        )}
      </tr>
    </>
  );
}

FileRow.propTypes = {
  type: PropTypes.shape({
    Icon: PropTypes.elementType.isRequired,
    label: PropTypes.string.isRequired,
    invalid: PropTypes.bool,
    success: PropTypes.bool,
  }).isRequired,
  file: PropTypes.instanceOf(File),
  setFile: PropTypes.func.isRequired,
  removeFile: PropTypes.func.isRequired,
  uploadFile: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

export default function MultiFileUploaderMobile({
  dossier,
  files: existingFiles,
  fetchFilesData,
}) {
  const apiEndpoint = useApiEndpoint();
  const [refreshValue, refresh] = useRefresh();
  const [fileMap, setFileMap] = useState({});
  const [loadingMap, setLoadingMap] = useState({});
  const [error, setError] = useState();

  const statut =
    statutsDossierGestionnaireToClient[dossier?.attributes?.statut];

  const existingTypes = useMemo(
    () =>
      existingFiles
        .filter(
          (f) =>
            f.attributes.dossier_statut === dossier?.attributes?.statut &&
            f.attributes.statut !== "Non conforme",
        )
        .map(({ attributes: file }) => file.type),
    [existingFiles, dossier],
  );

  const invalidTypes = useMemo(
    () =>
      existingFiles
        .filter((f) => f.attributes.statut === "Non conforme")
        .map(({ attributes: file }) => file.type),
    [existingFiles],
  );

  const fileTypeNames = typesDocumentsClientsStatuts[statut];

  const fileTypes = useMemo(
    () =>
      typesDocumentsClient
        .filter(({ label }) => fileTypeNames?.includes(label))
        .filter(({ isVisible }) => !isVisible || isVisible(dossier))
        .map(({ label, ...props }) => ({
          label,
          ...props,
          success: existingTypes.includes(
            typesDocumentsClientToGestionnaire[label],
          ),
          invalid: invalidTypes.includes(
            typesDocumentsClientToGestionnaire[label],
          ),
        })),
    [dossier, existingTypes, invalidTypes, fileTypeNames],
  );

  const setFile = useCallback((label, file) => {
    setFileMap((prev) => ({ ...prev, [label]: file }));
  }, []);

  const removeFile = useCallback((label) => {
    setFileMap((prev) => {
      const newMap = { ...prev };
      delete newMap[label];
      return newMap;
    });
  }, []);

  const uploadFile = useCallback(
    async (label) => {
      const file = fileMap[label];
      if (!file) return;

      setLoadingMap((prev) => ({ ...prev, [label]: true }));
      try {
        const formData = new FormData();
        formData.append("file1", file);
        formData.append("type1", typesDocumentsClientToGestionnaire[label]);

        const apiUrl = `${apiEndpoint}/api/dossier/upload-documents/${dossier.id}`;
        await axios.post(apiUrl, formData, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });

        removeFile(label);
        refresh();
      } catch (err) {
        setError(translateMessage(err.message));
      } finally {
        await fetchFilesData();
        setLoadingMap((prev) => ({ ...prev, [label]: false }));
      }
    },
    [apiEndpoint, dossier.id, fileMap, removeFile, refresh, fetchFilesData],
  );

  return fileTypes.length > 0 ? (
    <RefreshContext.Provider value={refreshValue}>
      <div className="w-full mb-12">
        <h1 className="md:text-lg mt-4 mb-3 font-semibold text-xs font-poppins">
          Mes documents à déposer
        </h1>
        <table className="mt-4 border border-x-0 border-t-1 border-b-0 w-full border-gray text-xs md:text-lg font-inter">
          <tbody>
            {fileTypes.map((type) => (
              <FileRow
                key={type.label}
                type={type}
                file={fileMap[type.label]}
                setFile={setFile}
                removeFile={removeFile}
                uploadFile={uploadFile}
                loading={loadingMap[type.label] || false}
              />
            ))}
          </tbody>
        </table>
        {error && (
          <div className="w-full mt-4 flex flex-row-reverse">
            <div className="p-4 font-semibold text-xs md:text-md text-red-500">
              {error}
            </div>
          </div>
        )}
      </div>
    </RefreshContext.Provider>
  ) : null;
}

MultiFileUploaderMobile.propTypes = {
  dossier: PropTypes.instanceOf(Object).isRequired,
  files: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
  fetchFilesData: PropTypes.func.isRequired,
};
